import { Option, Value } from 'baseui/select';
import stylesColors from 'common/styles/styles.colors';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import HeaderCheckboxHotelRescheduleAddons from './header-checkbox-hotel-reschedule-addons';
import {
  HotelRescheduleDetailAddOnsFormType,
  HotelRescheduleDetailFormType,
  HotelRescheduleDetailGuestFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

const name = 'hotelSales' as const;

function useHotelRescheduleAddOnsTableList(
  index: number,
): IColumn<HotelRescheduleDetailAddOnsFormType>[] {
  const { t } = useTranslation();
  const guestName = `${name}.${index}.hotelSaleGuests`;
  const guests: HotelRescheduleDetailGuestFormType[] = useWatch({
    name: guestName,
  });
  const guestOptions: Option[] = guests
    .filter((guest) => guest.guestId)
    .map((guest) => {
      return {
        id: guest.guestId,
        label: guest.guestName,
      };
    });

  return [
    {
      header: () => <HeaderCheckboxHotelRescheduleAddons index={index} />,
      accessorKey: 'isSelected',
      minSize: 50,
      size: 50,
      dataType: ColumnDataType.checkbox,
    },
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      options: guestOptions,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:price_@_unit'),
      accessorKey: 'priceUnit',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
    },
    {
      header: t('common:@_unit'),
      accessorKey: 'unit',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
    },
    {
      header: t('common:total_price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
  ];
}

export default function HotelRescheduleAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const { control, clearErrors, setValue, getValues } = useFormContext();
  const parentName = `${name}.${props.parentIndex}` as const;

  const detail: HotelRescheduleDetailFormType = useWatch({
    name: parentName,
  });

  const columns = useHotelRescheduleAddOnsTableList(props.parentIndex);

  const hotelSaleAddOnsName = `${parentName}.hotelSaleAddOns`;
  const { fields } = useFieldArray({
    control,
    name: hotelSaleAddOnsName,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex;
      const addOns = getValues(
        hotelSaleAddOnsName,
      ) as HotelRescheduleDetailAddOnsFormType[];
      const addOn = addOns[currentRowIndex];
      const columnId =
        action.columnId as keyof HotelRescheduleDetailAddOnsFormType;
      const extra = action.extra as Value;

      const otherVal: () => Partial<HotelRescheduleDetailAddOnsFormType> =
        () => {
          if (columnId === 'guestId') {
            return { guestName: extra?.[0]?.label as string };
          }
          return {};
        };

      if (action.type === 'update_cell') {
        addOns[currentRowIndex] = {
          ...addOn,
          [action.columnId]: action.value,
          ...otherVal(),
        };
        setValue(hotelSaleAddOnsName, addOns);
        clearErrors(`${hotelSaleAddOnsName}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, hotelSaleAddOnsName, setValue],
  );

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      <Separator gap={16} direction="vertical">
        <Text
          variant="LabelMedium"
          $style={{ color: stylesColors.primaryDefault }}
        >
          {t('document:addons')}
        </Text>
      </Separator>
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns}
          data={fields as unknown as HotelRescheduleDetailAddOnsFormType[]}
          dispatch={dispatch}
          name={hotelSaleAddOnsName}
          disabled={!editable || !detail.isSelected}
          uniqueRowKey="customId"
          noAddRemoveRow
          onCustomDisabledCell={({ accessorKey, rowOriginal }) => {
            if (accessorKey !== 'isSelected' && !rowOriginal.isSelected) {
              return true;
            }
            return false;
          }}
        />
      </div>
    </>
  );
}
